import { validatePhoneNumberCountry } from '@/constants/countryExclude';
import { calculateProfileCompletion } from '@/utils/profile/calculateProfileCompletion';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';
import API from '../../axios/API';
import { DataContext } from '../DataProviders/DataProvider';
import { NotificationContext } from '../Notifications/NotificationProvider';

const ProfileSettingContext = createContext();

export const ProfileSettingProvider = ({ children }) => {


    const { fetchNotificationData } = useContext(NotificationContext)
    const { setProfileComplete, setFLName, setProfileStatus, loginCustomerId, profileComplete, token } = useContext(DataContext);

    // Separate state for dynamic/preview completion (for UI display only)
    const [previewProfileComplete, setPreviewProfileComplete] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate(); // Fixed: removed destructuring
    const [validationComponentRefs, setValidationComponentRefs] = useState({});
    const [hasInteractedWithReferralCode, setHasInteractedWithReferralCode] = useState(false);
    const [initialPersonalDetails, setInitialPersonalDetails] = useState({});
    const [initialCompanyDetails, setInitialCompanyDetails] = useState({});
    const [initialWorkingHours, setInitialWorkingHours] = useState({});
    const [personalDetails, setPersonalDetails] = useState({
        customerId: "",
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobTitle: '',
        whatsApp: '',
        slack: '',
        supportMethod: '', // Add supportMethod to track user's selection
        profilePicture: '',
        referralCode: ''
    });
    const [companyDetails, setCompanyDetails] = useState({
        companyName: '',
        companyWebsite: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        taxId: ''
    });

    // Remove default values - make them empty initially
    const [workingHours, setWorkingHours] = useState({
        timeZone: '',
        startTime: '',
        endTime: ''
    });



    const defaultCountryShortCode = "US";
    const [country, setCountry] = useState(defaultCountryShortCode);

    // Form validation states - Initialize as false to trigger validation
    const [personalDetailsValid, setPersonalDetailsValid] = useState(false);
    const [companyDetailsValid, setCompanyDetailsValid] = useState(false);
    const [billingDetailsValid, setBillingDetailsValid] = useState(false);
    const [workingHoursValid, setWorkingHoursValid] = useState(false);
    const [cancelEditMode, setCancelEditMode] = useState(false);

    // Auto-progression state management
    const [hasAutoProgressed, setHasAutoProgressed] = useState(false);
    const [userManuallyNavigated, setUserManuallyNavigated] = useState(false);
    const autoProgressTimeoutRef = useRef(null);

    // Refs to prevent duplicate API calls
    const isFetchingUserDataRef = useRef(false);
    const isFetchingBillingEmailsRef = useRef(false);

    // Function to get the current profile completion for display (combines saved + preview)
    const getCurrentProfileCompletion = useCallback(() => {
        // If in edit mode and we have preview data, use preview data
        if (editMode && previewProfileComplete) {
            return previewProfileComplete;
        }
        // Otherwise use the saved profile completion
        return profileComplete;
    }, [editMode, previewProfileComplete, profileComplete]);

    // Function to calculate dynamic profile completion based on current form data
    const calculateDynamicProfileCompletion = useCallback((personalData, companyData, workingHoursData, billingValid) => {
        // 1. Personal Details
        const personalFields = ['firstName', 'lastName', 'phone', 'email', 'jobTitle'];
        const hasAllPersonalFields = personalFields.every(
            field => personalData[field] !== null && personalData[field] !== undefined && personalData[field] !== ''
        );
        const hasContactMethod = personalData.whatsApp || personalData.slack;
        const personalDetailsComplete = hasAllPersonalFields && !!hasContactMethod;

        // 2. Company Details
        const companyFields = ['companyName', 'companyWebsite'];
        const addressFields = ['addressLine1', 'city', 'state', 'zipCode'];
        const hasAllCompanyFields = companyFields.every(field =>
            companyData[field] !== null && companyData[field] !== undefined && companyData[field] !== ''
        );
        const hasAllAddressFields = addressFields.every(field =>
            companyData[field] !== null && companyData[field] !== undefined && companyData[field] !== ''
        );
        const hasCountry = country !== null && country !== undefined && country !== '';
        const companyDetailsComplete = hasAllCompanyFields && hasAllAddressFields && hasCountry;

        // 3. Billing Details
        const billingDetailsComplete = billingValid;

        // 4. Working Hours
        const workingHoursFields = ['timeZone', 'startTime', 'endTime'];
        const workingHoursComplete = workingHoursFields.every(field =>
            workingHoursData[field] !== null && workingHoursData[field] !== undefined && workingHoursData[field] !== ''
        );

        // Compute percentage (each section = 25%)
        const completedSections = [
            personalDetailsComplete,
            companyDetailsComplete,
            workingHoursComplete,
            billingDetailsComplete
        ].filter(Boolean).length;

        let totalPercentageProfileCompleted = completedSections * 25;
        totalPercentageProfileCompleted = Number(totalPercentageProfileCompleted.toFixed(0));

        return {
            personalDetailsComplete,
            companyDetailsComplete,
            billingDetailsComplete,
            workingHoursComplete,
            totalPercentageProfileCompleted
        };
    }, [country]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
        // Mark that user has manually navigated - disable auto-progression
        setUserManuallyNavigated(true);

        // Recalculate preview profile completion when manually changing tabs
        // This ensures the completion percentage is always accurate
        if (editMode) {
            const dynamicCompletion = calculateDynamicProfileCompletion(
                personalDetails,
                companyDetails,
                workingHours,
                billingDetailsValid
            );
            setPreviewProfileComplete(dynamicCompletion);
        }
    };

    // Smart auto-progression function with debouncing - memoized to prevent recreation
    const checkAndAutoProgress = useCallback((watchedValues, errors) => {
        if (!editMode || userManuallyNavigated) return;

        // Clear existing timeout
        if (autoProgressTimeoutRef.current) {
            clearTimeout(autoProgressTimeoutRef.current);
        }

        // Set a new timeout to debounce the auto-progression check
        const timeout = setTimeout(() => {
            // Get current profile completion percentage (use preview if in edit mode)
            const currentCompletion = getCurrentProfileCompletion();
            const currentProfileCompletion = currentCompletion?.totalPercentageProfileCompleted || 0;

            // Only auto-progress for new users (profile completion < 100%)
            // Don't auto-progress for users with 100% complete profiles who are just editing
            if (currentProfileCompletion >= 100) {
                return;
            }

            // Calculate completion percentage for Personal Details
            const personalRequiredFields = ['firstName', 'lastName', 'email', 'phone', 'jobTitle', 'supportMethod'];
            const personalFieldsFilled = personalRequiredFields.filter(field =>
                watchedValues[field] && watchedValues[field].toString().trim() !== ''
            ).length;

            // Check support method field completion
            let supportFieldFilled = false;
            if (watchedValues.supportMethod === 'whatsApp' && watchedValues.whatsApp) {
                supportFieldFilled = true;
            } else if (watchedValues.supportMethod === 'slack' && watchedValues.slack) {
                supportFieldFilled = true;
            }

            // Calculate total completion (6 base fields + 1 support field = 7 total)
            const totalFields = 7;
            const filledFields = personalFieldsFilled + (supportFieldFilled ? 1 : 0);
            const completionPercentage = (filledFields / totalFields) * 100;

            // Check for validation errors
            const hasErrors = !!(errors.firstName || errors.lastName || errors.email ||
                errors.phone || errors.jobTitle || errors.supportMethod ||
                errors.whatsApp || errors.slack);

            // Only auto-progress if 95%+ complete, no errors, and haven't manually navigated
            // Also ensure that all required fields are actually filled (not just partially typed)
            const allRequiredFieldsFilled = personalFieldsFilled === 6 && supportFieldFilled;

            if (completionPercentage >= 95 && !hasErrors && !hasAutoProgressed && tabIndex === 0 && allRequiredFieldsFilled) {
                // Calculate dynamic profile completion before auto-progressing
                const dynamicCompletion = calculateDynamicProfileCompletion(
                    { ...personalDetails, ...watchedValues },
                    companyDetails,
                    workingHours,
                    billingDetailsValid
                );

                // Update preview profile completion in context if personal details are now complete
                if (dynamicCompletion.personalDetailsComplete) {
                    setPreviewProfileComplete(dynamicCompletion);
                }

                setTabIndex(1);
                setHasAutoProgressed(true);
            }
        }, 1000); // 1 second debounce

        autoProgressTimeoutRef.current = timeout;
    }, [editMode, userManuallyNavigated, getCurrentProfileCompletion, tabIndex, hasAutoProgressed, calculateDynamicProfileCompletion, personalDetails, companyDetails, workingHours, billingDetailsValid]);

    // Auto-progression function for Company Details with debouncing - memoized to prevent recreation
    const checkAndAutoProgressCompanyDetails = useCallback((watchedValues, errors) => {
        if (!editMode || userManuallyNavigated) return;

        // Clear existing timeout
        if (autoProgressTimeoutRef.current) {
            clearTimeout(autoProgressTimeoutRef.current);
        }

        // Set a new timeout to debounce the auto-progression check
        const timeout = setTimeout(() => {
            // Get current profile completion percentage (use preview if in edit mode)
            const currentCompletion = getCurrentProfileCompletion();
            const currentProfileCompletion = currentCompletion?.totalPercentageProfileCompleted || 0;

            // Only auto-progress for new users (profile completion < 100%)
            if (currentProfileCompletion >= 100) {
                return;
            }

            // Reset hasAutoProgressed when on Company Details tab (tab 1) to allow auto-progression
            if (tabIndex === 1 && hasAutoProgressed) {
                setHasAutoProgressed(false);
            }

            // Calculate completion percentage for Company Details
            const companyRequiredFields = ['companyName', 'companyWebsite', 'addressLine1', 'city', 'state', 'zipCode'];
            const companyFieldsFilled = companyRequiredFields.filter(field =>
                watchedValues[field] && watchedValues[field].toString().trim() !== ''
            ).length;

            // Check country field separately - use watchedValues.country if available, otherwise use context country
            const countryValue = watchedValues.country || country;
            const countryFilled = countryValue && countryValue.toString().trim() !== '';

            // Calculate total completion (6 company fields + 1 country field = 7 total)
            const totalFields = 7;
            const filledFields = companyFieldsFilled + (countryFilled ? 1 : 0);
            const completionPercentage = (filledFields / totalFields) * 100;

            // Check for validation errors
            const hasErrors = !!(errors.companyName || errors.companyWebsite || errors.addressLine1 ||
                errors.addressLine2 || errors.city || errors.state || errors.zipCode || errors.country);
            // Only auto-progress if 95%+ complete, no errors, and haven't manually navigated
            // Also ensure that all required fields are actually filled (not just partially typed)
            const allRequiredFieldsFilled = companyFieldsFilled === 6 && countryFilled;

            if (completionPercentage >= 95 && !hasErrors && !hasAutoProgressed && tabIndex === 1 && allRequiredFieldsFilled) {
                // Calculate dynamic profile completion before auto-progressing
                const dynamicCompletion = calculateDynamicProfileCompletion(
                    personalDetails,
                    { ...companyDetails, ...watchedValues },
                    workingHours,
                    billingDetailsValid
                );

                // Update preview profile completion in context if company details are now complete
                if (dynamicCompletion.companyDetailsComplete) {
                    setPreviewProfileComplete(dynamicCompletion);
                }

                setTabIndex(2);
                setHasAutoProgressed(true);
            }
        }, 1000); // 1 second debounce

        autoProgressTimeoutRef.current = timeout;
    }, [editMode, userManuallyNavigated, getCurrentProfileCompletion, tabIndex, hasAutoProgressed, country, calculateDynamicProfileCompletion, personalDetails, companyDetails, workingHours, billingDetailsValid]);

    const handleEditClick = () => {
        if (editMode) {
            // Save changes - validate all forms
            handleSaveChanges();
        } else {
            // Enter edit mode
            setPasswordDialogOpen(true);
        }
        setHasInteractedWithReferralCode(false);
    };

    const handleEditModalClose = () => {
        setEditMode(false);
        setCancelEditMode(true);
        setValidationErrors([]);
        // Reset validation states when canceling edit mode
        setPersonalDetailsValid(false);
        setCompanyDetailsValid(false);
        setWorkingHoursValid(false);
        // Reset forms to original values if needed
        // You might want to implement a reset mechanism here
    };

    // Custom setEditMode function that also resets validation states
    const setEditModeWithValidation = (mode) => {
        setEditMode(mode);
        if (!mode) {
            // Only reset validation states when exiting edit mode (canceling)
            setPersonalDetailsValid(false);
            setCompanyDetailsValid(false);
            setWorkingHoursValid(false);
            setValidationErrors([]);
            // Clear preview completion when exiting edit mode
            setPreviewProfileComplete(null);
        } else {
            // Reset auto-progression states when entering edit mode
            setHasAutoProgressed(false);
            setUserManuallyNavigated(false);

            // Recalculate and update preview profile completion when entering edit mode
            // This ensures the completion percentage is accurate based on current form data
            const dynamicCompletion = calculateDynamicProfileCompletion(
                personalDetails,
                companyDetails,
                workingHours,
                billingDetailsValid
            );
            setPreviewProfileComplete(dynamicCompletion);
        }
    };

    // Helper function to validate email format
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Helper function to validate website format
    const isValidWebsite = (website) => {
        const websiteRegex = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s]*)?$/i;
        return websiteRegex.test(website)
    };

    // Helper function to validate name format (only letters)
    const isValidName = (name) => {
        const nameRegex = /^[A-Za-z]+$/;
        return nameRegex.test(name);
    };



    // Function to update profile completion dynamically (preview only)
    const updateProfileCompletion = useCallback(() => {
        if (editMode) {
            const dynamicCompletion = calculateDynamicProfileCompletion(
                personalDetails,
                companyDetails,
                workingHours,
                billingDetailsValid
            );
            // Use preview state for dynamic updates, not the main profile completion state
            setPreviewProfileComplete(dynamicCompletion);
        }
    }, [editMode, personalDetails, companyDetails, workingHours, billingDetailsValid, calculateDynamicProfileCompletion]);


    // Function to sync profile completion
    const syncProfileCompletion = useCallback(async () => {
        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
        if (!hasValidToken || !hasValidCustomerId) return;
        try {
            const response = await API.post(`${import.meta.env.BACKEND_API}/${loginCustomerId}/sync-completion`);
            if (response.status === 200) {
                setProfileStatus(true)
            }
        } catch (error) {
            console.log(error)
        }
    }, [loginCustomerId, setProfileStatus, token]);

    // Load user data on component mount
    useEffect(() => {
        if (loginCustomerId === "null" || !loginCustomerId) {
            return;
        }

        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
        if (hasValidToken || hasValidCustomerId) return;

        // Check if data is already loaded (e.g., by ProtectedRoute)
        // If personalDetails already has customerId matching loginCustomerId and firstName is set, skip API call
        if (personalDetails.customerId &&
            personalDetails.customerId.toString() === loginCustomerId.toString() &&
            personalDetails.firstName) {
            return; // Data already loaded, skip API call
        }

        // Prevent duplicate calls
        if (isFetchingUserDataRef.current) return;

        const loadUserData = async () => {
            try {
                isFetchingUserDataRef.current = true;
                const response = await API.get(`${import.meta.env.BACKEND_API}/getuser`, {
                    params: { customerId: loginCustomerId }
                });
                const userData = response.data;

                // Set personal details and initial data
                const personalData = {
                    customerId: userData.customerId || '',
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    jobTitle: userData.jobTitle || '',
                    whatsApp: userData.whatsApp || '',
                    slack: userData.slack || '',
                    supportMethod: userData.whatsApp ? 'whatsApp' : (userData.slack ? 'slack' : ''),
                    profilePicture: userData.profilePicture || '',
                    referralCode: userData.internalRefCodes?.find(ref => ref.key === 'ref_referral_code')?.value || ''
                };
                setPersonalDetails(personalData);
                setInitialPersonalDetails(personalData);

                // Set company details and initial data
                const companyData = {
                    companyName: userData.companyName || '',
                    companyWebsite: userData.companyWebsite || '',
                    addressLine1: userData.address?.addressLine1 || '',
                    addressLine2: (userData.address?.addressLine2 === "NA" ? "" : userData.address?.addressLine2) || '',
                    city: userData.address?.city || '',
                    state: userData.address?.state || '',
                    zipCode: userData.address?.zipCode || '',
                    country: userData.address?.country || '',
                    taxId: userData.taxId || ''
                };
                setCompanyDetails(companyData);
                setInitialCompanyDetails(companyData);

                // Set country
                if (userData.address?.country) {
                    setCountry(userData.address.country);
                }

                // Initialize working hours
                initializeWorkingHours(userData);

            } catch (error) {
                console.error('Error loading user data:', error);
            } finally {
                isFetchingUserDataRef.current = false;
            }
        };

        loadUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginCustomerId, token]);


    // Auto-update profile completion whenever form data changes
    useEffect(() => {
        updateProfileCompletion();
    }, [updateProfileCompletion]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (autoProgressTimeoutRef.current) {
                clearTimeout(autoProgressTimeoutRef.current);
            }
        };
    }, []);

    // Fetch billing emails
    useEffect(() => {
        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
        if (!hasValidToken || !hasValidCustomerId) return;

        // Prevent duplicate calls
        if (isFetchingBillingEmailsRef.current) return;

        const fetchBillingEmail = async () => {
            try {
                isFetchingBillingEmailsRef.current = true;
                const response = await API.get(`${import.meta.env.BACKEND_API}/${loginCustomerId}/billing-emails`);
                // Filter out null/undefined values and check if there's at least one valid email
                const validBillingEmails = Array.isArray(response.data)
                    ? response.data.filter(email => email != null && email !== '')
                    : [];
                if (validBillingEmails.length > 0) {
                    setProfileStatus(true)
                    setBillingDetailsValid(true); // Set validation to true when valid emails exist
                } else {
                    setBillingDetailsValid(false); // Set validation to false when no valid emails
                }
            } catch (error) {
                console.log(error)
                setBillingDetailsValid(false); // Set validation to false on error
            } finally {
                isFetchingBillingEmailsRef.current = false;
            }
        }

        fetchBillingEmail()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginCustomerId, token]);


    const validateForm = async (skipWorkingHours = false) => {
        const errors = [];
        const missingFields = [];
        const formatErrors = [];

        // Validate personal details - check actual data instead of just validation state
        const personalRequiredFields = ['firstName', 'lastName', 'email', 'phone', 'jobTitle', 'supportMethod'];
        const personalMissingFields = personalRequiredFields.filter(field =>
            !personalDetails[field] || personalDetails[field].toString().trim() === ''
        );

        // Validate WhatsApp/Slack based on support method
        // Use the supportMethod from context to determine which field is required
        if (personalDetails.supportMethod === 'whatsApp') {
            if (!personalDetails.whatsApp || personalDetails.whatsApp.toString().trim() === '') {
                personalMissingFields.push('whatsApp');
            }
        } else if (personalDetails.supportMethod === 'slack') {
            if (!personalDetails.slack || personalDetails.slack.toString().trim() === '') {
                personalMissingFields.push('slack');
            }
        }

        if (personalMissingFields.length > 0) {
            errors.push("Please complete all required fields in Personal Details");
            personalMissingFields.forEach(field => {
                missingFields.push(`Personal Details: ${field}`);
            });
        }

        // Validate format of personal details
        if (personalDetails.firstName && !isValidName(personalDetails.firstName.trim())) {
            formatErrors.push("First name must contain only letters (no spaces or special characters)");
        }

        if (personalDetails.lastName && !isValidName(personalDetails.lastName.trim())) {
            formatErrors.push("Last name must contain only letters (no spaces or special characters)");
        }

        if (personalDetails.email && !isValidEmail(personalDetails.email)) {
            formatErrors.push("Please enter a valid email address");
        }

        if (personalDetails.phone && !isValidPhoneNumber(personalDetails.phone)) {
            formatErrors.push("Please enter a valid phone number");
        }

        if (personalDetails.whatsApp && !isValidPhoneNumber(personalDetails.whatsApp)) {
            formatErrors.push("Please enter a valid WhatsApp number");
        }

        // Validate phone number country restrictions
        if (personalDetails.phone) {
            const phoneValidation = validatePhoneNumberCountry(personalDetails.phone);
            if (!phoneValidation.isValid) {
                formatErrors.push(phoneValidation.error);
            }
        }

        if (personalDetails.whatsApp) {
            const whatsAppValidation = validatePhoneNumberCountry(personalDetails.whatsApp);
            if (!whatsAppValidation.isValid) {
                formatErrors.push(whatsAppValidation.error);
            }
        }


        // Validate company details - check actual data instead of just validation state
        const companyRequiredFields = ['companyName', 'companyWebsite', 'addressLine1', 'city', 'state', 'zipCode'];
        const companyMissingFields = companyRequiredFields.filter(field =>
            !companyDetails[field] || companyDetails[field].toString().trim() === ''
        );

        // Check country separately
        if (!country || country.toString().trim() === '') {
            companyMissingFields.push('country');
        }

        if (companyMissingFields.length > 0) {
            errors.push("Please complete all required fields in Company Details");
            companyMissingFields.forEach(field => {
                missingFields.push(`Company Details: ${field}`);
            });
        }

        // Validate format of company details
        if (companyDetails.companyWebsite && !isValidWebsite(companyDetails.companyWebsite)) {
            formatErrors.push("Please enter a valid website URL");
        }

        // Validate working hours - check actual data instead of just validation state
        const workingHoursRequiredFields = ['timeZone', 'startTime', 'endTime'];
        const workingHoursMissingFields = workingHoursRequiredFields.filter(field =>
            !workingHours[field] || workingHours[field].toString().trim() === ''
        );

        if (workingHoursMissingFields.length > 0 && !skipWorkingHours) {
            errors.push("Please set your working hours");
            workingHoursMissingFields.forEach(field => {
                missingFields.push(`Working Hours: ${field}`);
            });
        }

        // Validate working hours time logic - end time must be after start time
        if (workingHours.startTime && workingHours.endTime && !skipWorkingHours) {
            const toMinutes = (timeStr) => {
                const [h, m] = timeStr.split(':').map(Number);
                return h * 60 + m;
            };

            const startMinutes = toMinutes(workingHours.startTime);
            const endMinutes = toMinutes(workingHours.endTime);

            if (endMinutes <= startMinutes) {
                formatErrors.push("End time must be after start time");
                missingFields.push("Working Hours: end time must be after start time");
            }
        }

        // Validate billing details - check if at least one valid billing email exists
        try {
            const billingResponse = await API.get(`${import.meta.env.BACKEND_API}/${loginCustomerId}/billing-emails`);
            // Filter out null/undefined values and check if there's at least one valid email
            const validBillingEmails = Array.isArray(billingResponse.data)
                ? billingResponse.data.filter(email => email != null && email !== '')
                : [];
            if (validBillingEmails.length === 0) {
                errors.push("Please add at least one billing email address");
                missingFields.push("Billing Details: billing email address");
            }
        } catch (error) {
            // If API call fails, fall back to state check
            if (!billingDetailsValid) {
                errors.push("Please add at least one billing email address");
                missingFields.push("Billing Details: billing email address");
            }
        }

        // Validate referral code if it has a value (always validate on save, not just if user interacted)
        // If user has interacted with it, we know they want to use it, so validate it
        // If user hasn't interacted but there's a value, it might be from a previous save, so validate it too
        if (personalDetails.referralCode && personalDetails.referralCode.trim() !== '') {
            const hasValidToken = token && token !== 'null' && token !== 'undefined';
            if (!hasValidToken) {
                formatErrors.push("Your session has expired. Please log in again.");
            } else {
                try {
                    const response = await API.get(`${import.meta.env.BACKEND_API}/referral/affiliate/referral/check?code=${personalDetails.referralCode.trim()}&customerId=${loginCustomerId}`);
                    if (response.data !== true) {
                        formatErrors.push("Invalid referral code - please check and try again");
                        missingFields.push("Personal Details: referralCode");
                    }
                } catch (error) {
                    console.error("Referral code validation error:", error);
                    formatErrors.push("Unable to validate referral code - please try again");
                    missingFields.push("Personal Details: referralCode");
                }
            }
        }

        // Combine all errors
        const allErrors = [...errors, ...formatErrors];

        // Log missing fields for debugging
        // if (missingFields.length > 0) {
        //     // console.log('Missing required fields:', missingFields);
        // }

        // if (formatErrors.length > 0) {
        //     // console.log('Format validation errors:', formatErrors);
        // }

        return { errors: allErrors, missingFields, formatErrors };
    };

    const handleSaveChanges = async (showLoading = true, skipWorkingHours = false) => {
        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
        if (!hasValidToken || !hasValidCustomerId) {
            setValidationErrors(["Your session has expired. Please log in again."]);
            return false;
        }

        // Trigger form validations for all tabs before validating
        // This ensures form validation state is up to date even if we're on a different tab
        if (validationComponentRefs.personalDetails?.forceShowErrors) {
            await validationComponentRefs.personalDetails.forceShowErrors();
        }
        if (validationComponentRefs?.companyDetails?.forceShowErrors) {
            await validationComponentRefs.companyDetails.forceShowErrors();
        }
        if (!skipWorkingHours && validationComponentRefs.workingHours?.forceShowErrors) {
            await validationComponentRefs.workingHours.forceShowErrors();
        }

        const { errors, missingFields } = await validateForm(skipWorkingHours);

        if (errors.length > 0) {
            setValidationErrors(errors);
            if (!skipWorkingHours && validationComponentRefs.workingHours?.forceShowErrors) {
                validationComponentRefs.workingHours.forceShowErrors();
            }

            if (validationComponentRefs.personalDetails?.forceShowErrors) {
                validationComponentRefs.personalDetails.forceShowErrors();
            }
            if (validationComponentRefs?.companyDetails?.forceShowErrors) {
                validationComponentRefs.companyDetails.forceShowErrors();
            }

            // Auto-navigate to first incomplete tab based on missing fields
            const hasPersonalMissing = missingFields.some(field => field.startsWith('Personal Details:'));
            const hasCompanyMissing = missingFields.some(field => field.startsWith('Company Details:'));
            const hasBillingMissing = missingFields.some(field => field.startsWith('Billing Details:'));
            const hasWorkingHoursMissing = missingFields.some(field => field.startsWith('Working Hours:'));

            // Check if there's a referral code error in errors array
            const hasReferralCodeError = errors.some(error =>
                error.includes('referral code') || error.toLowerCase().includes('referral')
            );

            if (hasPersonalMissing || hasReferralCodeError) {
                setTabIndex(0);
            } else if (hasCompanyMissing) {
                setTabIndex(1);
            } else if (hasBillingMissing) {
                setTabIndex(2);
            } else if (hasWorkingHoursMissing) {
                // Working hours is in the sidebar, not a tab
            }

            return false;
        }

        // All validations passed - save the form
        if (showLoading) setIsLoading(true)
        try {
            const requestBody = {
                customerId: loginCustomerId,
                firstName: personalDetails.firstName,
                lastName: personalDetails.lastName,
                profilePicture: personalDetails.profilePicture,
                email: personalDetails.email.toLowerCase(),
                phone: personalDetails.phone,
                companyName: companyDetails.companyName,
                companyWebsite: companyDetails.companyWebsite,
                jobTitle: personalDetails.jobTitle.trim(),
                address: {
                    addressLine1: companyDetails?.addressLine1,
                    addressLine2: companyDetails?.addressLine2?.trim() || "NA",
                    city: companyDetails?.city,
                    state: companyDetails?.state,
                    country: country, // Use the country from state
                    zipCode: companyDetails?.zipCode,
                },
                taxId: companyDetails.taxId,
                isEmailVerified: true,
                isTermsAccepted: personalDetails.isTermsAccepted,
                isProfileComplete: true,
                whatsApp: personalDetails.whatsApp,
                slack: personalDetails.slack,
                internalRefCodes: [
                    {
                        "key": "ref_referral_code",
                        "value": personalDetails?.referralCode
                    }
                ],
                workingHours: {
                    customerId: loginCustomerId,
                    timeZone: workingHours.timeZone,
                    start: convertTimeToNumber(workingHours.startTime),
                    end: convertTimeToNumber(workingHours.endTime)
                }
            };
            const response = await API.post(
                `${import.meta.env.BACKEND_API}/userprofile`,
                requestBody
            );
            setInitialCompanyDetails(companyDetails)
            setInitialPersonalDetails(personalDetails)
            setInitialWorkingHours(workingHours)

            await syncProfileCompletion()

            const newProfileCompletion = calculateProfileCompletion(response.data);
            setProfileComplete(newProfileCompletion);
            setFLName(personalDetails.firstName + " " + personalDetails.lastName);
            setHasInteractedWithReferralCode(false);

            // Only trigger modal if reaching 100% for the first time AND silent save is NOT active AND NOT skipping working hours
            if (showLoading && !skipWorkingHours && newProfileCompletion.totalPercentageProfileCompleted >= 100 && !isShownProfileCompleted) {
                setOpen(true);
                setIsShownProfileCompleted(true);
                localStorage.setItem("isShownProfileCompleted", "true");
            }

            if (showLoading) {
                setEditMode(false);
            }
            setValidationErrors([]);
            // Clear preview completion after successful save
            setPreviewProfileComplete(null);
            fetchNotificationData();
            return true;
        } catch (error) {
            console.error("Error saving profile data:", error);
            setValidationErrors(["Failed to save profile data. Please try again later."]);
            return false;
        } finally {
            if (showLoading) setIsLoading(false)
        }
    };

    const [open, setOpen] = useState(false);
    const [isShownProfileCompleted, setIsShownProfileCompleted] = useState(
        localStorage.getItem("isShownProfileCompleted") === "true" // Convert string to boolean
    );

    // Helper function to convert time format (HH:MM) to number format (HHMM)
    const convertTimeToNumber = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return null;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return parseInt(`${hours.toString().padStart(2, '0')}${minutes.toString().padStart(2, '0')}`);
    };

    const handleCreateOrder = () => {
        navigate("/orders-view");
        setOpen(false);
    };

    // Function to update personal details with dynamic completion calculation
    const updatePersonalDetails = (newDetails) => {
        setPersonalDetails(prev => {
            const updatedDetails = { ...prev, ...newDetails };

            // Calculate dynamic profile completion when personal details change
            if (editMode) {
                const dynamicCompletion = calculateDynamicProfileCompletion(
                    updatedDetails,
                    companyDetails,
                    workingHours,
                    billingDetailsValid
                );

                // Update preview profile completion if personal details are now complete
                if (dynamicCompletion.personalDetailsComplete) {
                    setPreviewProfileComplete(dynamicCompletion);
                }
            }

            return updatedDetails;
        });
    };

    // Function to update company details with dynamic completion calculation
    const updateCompanyDetails = (newDetails) => {
        setCompanyDetails(prev => {
            const updatedDetails = { ...prev, ...newDetails };

            // Calculate dynamic profile completion when company details change
            if (editMode) {
                const dynamicCompletion = calculateDynamicProfileCompletion(
                    personalDetails,
                    updatedDetails,
                    workingHours,
                    billingDetailsValid
                );

                // Update preview profile completion if company details are now complete
                if (dynamicCompletion.companyDetailsComplete) {
                    setPreviewProfileComplete(dynamicCompletion);
                }
            }

            return updatedDetails;
        });
    };

    // Function to update working hours with dynamic completion calculation
    const updateWorkingHours = (newHours) => {
        setWorkingHours(prev => {
            const updatedHours = { ...prev, ...newHours };

            // Calculate dynamic profile completion when working hours change
            if (editMode) {
                const dynamicCompletion = calculateDynamicProfileCompletion(
                    personalDetails,
                    companyDetails,
                    updatedHours,
                    billingDetailsValid
                );

                // Update preview profile completion if working hours are now complete
                if (dynamicCompletion.workingHoursComplete) {
                    setPreviewProfileComplete(dynamicCompletion);
                }
            }

            return updatedHours;
        });
    };

    // Function to initialize working hours from user data
    const initializeWorkingHours = (userData) => {
        if (userData?.workingHours) {
            const { timeZone, start, end } = userData.workingHours;

            // Convert backend format to frontend format
            const formatTimeFromBackend = (timeValue) => {
                if (timeValue === null || timeValue === undefined || timeValue === '') return '';
                if (typeof timeValue === 'number') {
                    const timeStr = timeValue.toString().padStart(4, '0');
                    const hours = timeStr.substring(0, 2);
                    const minutes = timeStr.substring(2, 4);
                    return `${hours}:${minutes}`;
                }
                return timeValue;
            };

            setWorkingHours({
                timeZone: timeZone || '',
                startTime: formatTimeFromBackend(start),
                endTime: formatTimeFromBackend(end)
            });
        }
    };

    // Function to fetch and populate profile data for impersonated user
    const fetchAndPopulateProfileData = async (customerId) => {
        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        if (!hasValidToken || !customerId) return;
        try {
            const response = await API.get(`${import.meta.env.BACKEND_API}/getuser`, {
                params: { customerId: customerId }
            });

            const userData = response.data;

            // Update personal details
            const personalData = {
                customerId: userData.customerId || '',
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
                phone: userData.phone || '',
                jobTitle: userData.jobTitle || '',
                whatsApp: userData.whatsApp || '',
                slack: userData.slack || '',
                supportMethod: userData.whatsApp ? 'whatsApp' : (userData.slack ? 'slack' : ''),
                profilePicture: userData.profilePicture || '',
                referralCode: userData.internalRefCodes?.find(ref => ref.key === 'ref_referral_code')?.value || ''
            };
            setPersonalDetails(personalData);
            setInitialPersonalDetails(personalData); // Set initial data for reset functionality

            // Update company details
            const companyData = {
                companyName: userData.companyName || '',
                companyWebsite: userData.companyWebsite || '',
                addressLine1: userData.address?.addressLine1 || '',
                addressLine2: (userData.address?.addressLine2 === "NA" ? "" : userData.address?.addressLine2) || '',
                city: userData.address?.city || '',
                state: userData.address?.state || '',
                zipCode: userData.address?.zipCode || '',
                country: userData.address?.country || '',
                taxId: userData.taxId || ''
            };
            setCompanyDetails(companyData);
            setInitialCompanyDetails(companyData); // Set initial data for reset functionality

            // Update country
            if (userData.address?.country) {
                setCountry(userData.address.country);
            }

            // Initialize working hours
            initializeWorkingHours(userData);

            // Update FLName in DataContext for avatar initials
            setFLName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim());

        } catch (error) {
            console.error('Error fetching profile data for impersonated user:', error);
        }
    };

    return (
        <ProfileSettingContext.Provider
            value={{
                tabIndex,
                setTabIndex,
                passwordDialogOpen,
                setPasswordDialogOpen,
                editMode,
                setEditMode: setEditModeWithValidation, // Use custom setter
                validationErrors,
                setValidationErrors,
                personalDetailsValid,
                setPersonalDetailsValid,
                companyDetailsValid,
                setCompanyDetailsValid,
                workingHoursValid,
                setWorkingHoursValid,
                handleTabChange,
                handleEditClick,
                handleEditModalClose,
                handleSaveChanges,
                open,
                setOpen,
                handleCreateOrder,
                personalDetails,
                setPersonalDetails,
                companyDetails,
                setCompanyDetails,
                workingHours,
                setWorkingHours,
                updatePersonalDetails,
                updateCompanyDetails,
                updateWorkingHours,
                initializeWorkingHours,
                setCountry,
                country,
                isLoading,
                setIsLoading,
                setIsShownProfileCompleted,
                isShownProfileCompleted,
                validateForm, // Export this for debugging
                validationComponentRefs,
                setValidationComponentRefs,
                hasInteractedWithReferralCode,
                setHasInteractedWithReferralCode,
                fetchAndPopulateProfileData,
                billingDetailsValid,
                setBillingDetailsValid,
                initialPersonalDetails,
                setInitialPersonalDetails,
                initialCompanyDetails,
                setInitialCompanyDetails,
                initialWorkingHours,
                setInitialWorkingHours,
                cancelEditMode,
                checkAndAutoProgress,
                checkAndAutoProgressCompanyDetails,
                calculateDynamicProfileCompletion,
                updateProfileCompletion,
                getCurrentProfileCompletion,
                previewProfileComplete
            }}
        >
            {children}
        </ProfileSettingContext.Provider>
    );
};

export const useProfileSetting = () => useContext(ProfileSettingContext);